import { configureStore, Reducer } from "@reduxjs/toolkit";
import appCoreReducer, { AppCoreState, forgetSnackbarWhenPersisted } from "./AppCoreReducer";
import appAuthReducer, { AppAuthState } from "./AppAuthReducer";
import appOperationsReducer, { AppOperationsState } from "./AppOperationsReducer";
import localForage from "localforage";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistCombineReducers,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppReducersMap = Record<string, Reducer<any, any>>;

interface AppStoreConfig<TAppReducers extends AppReducersMap> {
  key: string;
  version: number;
  reducers: TAppReducers;
  whitelist?: string[];
}

export function createAppStore<TAppReducers extends AppReducersMap>(
  config: AppStoreConfig<TAppReducers>,
) {
  type AppState = {
    [K in keyof TAppReducers]: ReturnType<TAppReducers[K]>;
  };

  type CombinedState = AppState & {
    core: AppCoreState;
    auth: AppAuthState;
    operations: AppOperationsState;
  };

  const persistConfig = {
    key: config.key,
    storage: localForage,
    version: config.version,
    whitelist: ["core", ...(config.whitelist ?? [])],
    transforms: [forgetSnackbarWhenPersisted],
  };

  const allReducers = {
    ...config.reducers,
    core: appCoreReducer,
    auth: appAuthReducer,
    operations: appOperationsReducer,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rootReducer = persistCombineReducers<CombinedState>(persistConfig, allReducers as any);

  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  });

  const persistor = persistStore(store);

  return { store, persistor };
}
